import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { Button, IconButton, MenuItem, TextField, Chip } from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { useFormik } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';
import DocumentFileInput from 'components/DocumentFileInput';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';
import { FormSchema } from './schemas/ReceptionForm';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getVenueWarehouses } from 'services/stockWarehouseServices';
import { getVenueProductionCenters } from 'services/stockProductionCenterServices';
import { getVenueSuppliers } from 'services/supplierServices';
import { getVenueCatalog } from 'services/stockServices';
import { storeStockReception, updateStockReception } from 'services/stockReceptionServices';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';
import Venue from 'types/models/Venue';
import {
  StockReception,
  StockSupplier,
  StockWarehouse,
  StockProductionCenter,
  StockOriginType,
  StockProductType,
  StockReceptionItem,
  ReceiptStatus,
  StockWarehouseType,
  UnitOfMeasure,
} from 'types/models/StockReceipt';
import { formatMoneyAuto } from 'utils/formatMoney';

interface Props {
  reception?: StockReception | null;
  isReadOnly?: boolean;
}

interface VenueCatalogProduct {
  globalProductId: number;
  sku: string;
  name: string;
  type: StockProductType;
  unitMeasure?: UnitOfMeasure | null;
  receptionUnitMeasure?: UnitOfMeasure | null;
  unitsPerReceptionUnit?: number | null;
}

const EMPTY_PRODUCT: StockReceptionItem = {
  id: '',
  globalProductId: null,
  sku: '',
  name: '',
  quantity: 1,
  unitCost: 0,
  type: StockProductType.FINISHED,
  unitMeasure: null,
  receptionUnitMeasure: UnitOfMeasure.UNIT,
  unitsPerReceptionUnit: 1,
};

const STATUS_LABEL: Record<ReceiptStatus, string> = {
  [ReceiptStatus.DRAFT]: 'status.draft',
  [ReceiptStatus.CONFIRMED]: 'status.confirmed',
  [ReceiptStatus.CANCELLED]: 'status.cancelled',
};

export default ({ reception, isReadOnly }: Props): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [openDialog, setOpenDialog] = React.useState(false);

  const [suppliers, setSuppliers] = React.useState<StockSupplier[]>([]);
  const [warehouses, setWarehouses] = React.useState<StockWarehouse[]>([]);
  const [productionCenters, setProductionCenters] = React.useState<StockProductionCenter[]>([]);
  const [venues, setVenues] = React.useState<Venue[]>([]);

  const [catalogProducts, setCatalogProducts] = React.useState<VenueCatalogProduct[]>([]);
  const [products, setProducts] = React.useState<StockReceptionItem[]>(
    reception?.items?.length ? reception.items : [{ ...EMPTY_PRODUCT }],
  );

  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [removedReceiptFile, setRemovedReceiptFile] = React.useState(false);
  const [removedPurchaseOrderFile, setRemovedPurchaseOrderFile] = React.useState(false);

  const formRef = useAutoFocusFirstField(!isLoading && !loadError);

  React.useEffect(() => {
    let mounted = true;

    async function loadInitialData() {
      if (isVenueEditor) {
        if (mounted) setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);

      try {
        const [venuesData] = await Promise.all([getVenuesWithStockModuleActive()]);

        if (!mounted) return;

        setVenues(venuesData?.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueId: reception?.venueId ?? entity?.id ?? '',
      receivedAt: reception?.receivedAt ?? moment().format('YYYY-MM-DD'),
      receiptFile: null,
      purchaseOrderFile: null,

      originType: reception?.originType ?? StockOriginType.SUPPLIER,
      originSupplierId: reception?.originSupplierId ?? '',
      originProductionCenterId: reception?.originProductionCenterId ?? '',

      destinationWarehouseId: reception?.destinationWarehouseId ?? '',

      observations: reception?.observations ?? '',
    },
    validationSchema: () => FormSchema(t),
    onSubmit: () => {
      setOpenDialog(true);
    },
  });

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setCatalogProducts([]);
      return;
    }

    async function loadCatalog() {
      try {
        const response = await getVenueCatalog(formik.values.venueId);

        setCatalogProducts(response.products ?? []);
      } catch (error) {
        console.error(error);
        setCatalogProducts([]);
      }
    }
    async function loadVenueSuppliers() {
      try {
        const response = await getVenueSuppliers(formik.values.venueId);
        setSuppliers(response);
      } catch (error) {
        console.error(error);
        setSuppliers([]);
      }
    }
    async function loadVenueWarehouses() {
      try {
        const response = await getVenueWarehouses(formik.values.venueId, { type: StockWarehouseType.CENTRAL });
        setWarehouses(response);

        if (!reception && !formik.values.destinationWarehouseId && response.length === 1) {
          formik.setFieldValue('destinationWarehouseId', response[0].id);
        }
      } catch (error) {
        console.error(error);
        setWarehouses([]);
      }
    }
    async function loadVenueProductionCenters() {
      try {
        const response = await getVenueProductionCenters(formik.values.venueId);
        setProductionCenters(response);
      } catch (error) {
        console.error(error);
        setProductionCenters([]);
      }
    }

    loadCatalog();
    loadVenueSuppliers();
    loadVenueWarehouses();
    loadVenueProductionCenters();
  }, [formik.values.venueId]);

  const totalReceipt = React.useMemo(() => {
    return products.reduce((acc, product) => {
      return acc + product.quantity * product.unitCost;
    }, 0);
  }, [products]);

  function updateProduct(index: number, key: keyof StockReceptionItem, value: any) {
    const copy = [...products];

    copy[index] = {
      ...copy[index],
      [key]: value,
    };

    setProducts(copy);
  }

  function addProduct() {
    setProducts((prev) => [
      ...prev,
      {
        ...EMPTY_PRODUCT,
      },
    ]);
  }

  function removeProduct(index: number) {
    if (products.length === 1) {
      return;
    }

    setProducts(products.filter((_, i) => i !== index));
  }

  function selectCatalogProduct(index: number, globalProductId: number) {
    const product = catalogProducts.find((p) => p.globalProductId === globalProductId);

    if (!globalProductId) {
      setProducts((prev) => {
        const copy = [...prev];
        copy[index] = {
          ...copy[index],
          globalProductId: null,
        };
        return copy;
      });
      return;
    }
    if (!product) return;

    setProducts((prev) => {
      const copy = [...prev];

      copy[index] = {
        ...copy[index],
        globalProductId: product.globalProductId,
        sku: product.sku,
        name: product.name,
        type: product.type,
        // Producto existente → unidades read-only, precargadas desde el catálogo.
        unitMeasure: product.unitMeasure ?? null,
        receptionUnitMeasure: product.receptionUnitMeasure ?? UnitOfMeasure.UNIT,
        unitsPerReceptionUnit: product.unitsPerReceptionUnit ?? 1,
      };

      return copy;
    });
  }

  function clearProduct(index: number) {
    const copy = [...products];

    copy[index] = {
      ...EMPTY_PRODUCT,
    };

    setProducts(copy);
  }

  async function onConfirm(): Promise<void> {
    setSaveError(null);

    try {
      const values = formik.values;
      const payload = new FormData();

      payload.append('venueId', values.venueId);
      payload.append('receivedAt', values.receivedAt ? moment(values.receivedAt).toISOString() : '');
      payload.append('originType', values.originType);
      if (values.originSupplierId) {
        payload.append('originSupplierId', String(values.originSupplierId));
      }
      if (values.originProductionCenterId) {
        payload.append('originProductionCenterId', String(values.originProductionCenterId));
      }
      payload.append('destinationWarehouseId', values.destinationWarehouseId);
      payload.append('observations', values.observations ?? '');

      if (values.receiptFile) {
        payload.append('receiptFile', values.receiptFile);
      } else if (reception && reception.receiptFileUrl && removedReceiptFile) {
        payload.append('removeReceiptFile', '1');
      }
      if (values.purchaseOrderFile) {
        payload.append('purchaseOrderFile', values.purchaseOrderFile);
      } else if (reception && reception.purchaseOrderFileUrl && removedPurchaseOrderFile) {
        payload.append('removePurchaseOrderFile', '1');
      }

      products.forEach((product, index) => {
        payload.append(`products[${index}][name]`, product.name);
        payload.append(`products[${index}][sku]`, product.sku ?? '');
        payload.append(`products[${index}][quantity]`, String(product.quantity));
        payload.append(`products[${index}][unitCost]`, String(product.unitCost));
        payload.append(`products[${index}][type]`, product.type);
        payload.append(
          `products[${index}][receptionUnitMeasure]`,
          product.receptionUnitMeasure ? String(product.receptionUnitMeasure) : '',
        );
        if (product.type === StockProductType.RAW_MATERIAL) {
          payload.append(`products[${index}][unitMeasure]`, product.unitMeasure ? String(product.unitMeasure) : '');
          payload.append(
            `products[${index}][unitsPerReceptionUnit]`,
            product.unitsPerReceptionUnit !== undefined && product.unitsPerReceptionUnit !== null
              ? String(product.unitsPerReceptionUnit)
              : '',
          );
        }
      });

      if (reception) {
        await updateStockReception(reception.id, payload);
      } else {
        await storeStockReception(payload);
      }
      history.go(-1);
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      formik.setSubmitting(false);
    }
  }

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      {reception && <Chip size="small" variant="outlined" label={t(STATUS_LABEL[reception.status])} />}

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.forms.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        <TextField
          margin="dense"
          fullWidth
          name="receivedAt"
          label={t('stock.receipts.form.received-at')}
          value={formik.values.receivedAt ? moment(formik.values.receivedAt).format('YYYY-MM-DD') : ''}
          onChange={(event) => {
            const val = event.target.value;
            formik.setFieldValue('receivedAt', val ? moment(val).toDate() : null);
          }}
          error={formik.touched.receivedAt && Boolean(formik.errors.receivedAt)}
          helperText={formik.touched.receivedAt && formik.errors.receivedAt}
          disabled={formik.isSubmitting}
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
        />

        <SelectField
          name="originType"
          label={t('stock.receipts.form.origin')}
          placeholder={t('stock.receipts.form.origin-placeholder')}
          value={formik.values.originType}
          options={[
            {
              label: t('stock.location-label.SUPPLIER'),
              value: StockOriginType.SUPPLIER,
            },
            {
              label: t('stock.location-label.PRODUCTION_CENTER'),
              value: StockOriginType.PRODUCTION_CENTER,
            },
          ]}
          isMulti={false}
          setFieldValue={(name, value) => {
            formik.setFieldValue(name, value);
            if (!reception) {
              formik.setFieldValue('originSupplierId', '');
              formik.setFieldValue('originProductionCenterId', '');
            }
          }}
          onBlur={formik.handleBlur}
          touched={formik.touched.originType}
          error={formik.errors.originType}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />

        {formik.values.originType === StockOriginType.SUPPLIER && (
          <SelectField
            name="originSupplierId"
            label={t('stock.location-label.SUPPLIER')}
            placeholder={t('stock.receipts.form.supplier-placeholder')}
            value={formik.values.originSupplierId}
            options={mapToSelectOptions(suppliers, {
              label: 'businessName',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.originSupplierId}
            error={formik.errors.originSupplierId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        {formik.values.originType === StockOriginType.PRODUCTION_CENTER && (
          <SelectField
            name="originProductionCenterId"
            label={t('stock.location-label.PRODUCTION_CENTER')}
            placeholder={t('stock.receipts.form.production-center-placeholder')}
            value={formik.values.originProductionCenterId}
            options={mapToSelectOptions(productionCenters, {
              label: 'name',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.originProductionCenterId}
            error={formik.errors.originProductionCenterId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        <SelectField
          name="destinationWarehouseId"
          label={t('stock.receipts.table.destination-warehouse')}
          placeholder={t('stock.receipts.form.warehouse-placeholder')}
          value={formik.values.destinationWarehouseId}
          options={mapToSelectOptions(warehouses, {
            label: 'name',
          })}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.destinationWarehouseId}
          error={formik.errors.destinationWarehouseId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable
        />

        <div className="my-6">
          <DocumentFileInput
            label={t('stock.receipts.form.purchase-order')}
            name="purchaseOrderFile"
            value={formik.values.purchaseOrderFile}
            setFieldValue={(field, fileValue) => {
              formik.setFieldValue(field, fileValue);
              if (fileValue) setRemovedPurchaseOrderFile(false);
            }}
            isSubmitting={formik.isSubmitting}
            touched={formik.touched.purchaseOrderFile}
            errors={formik.errors.purchaseOrderFile}
            accept=".pdf,.jpg,.jpeg,.png,.csv,.doc,.docx,.xls,.xlsx"
            existingUrl={removedPurchaseOrderFile ? null : reception?.purchaseOrderFileUrl}
            existingFileLabel={t('stock.receipts.form.current-file')}
            onRemoveExisting={() => setRemovedPurchaseOrderFile(true)}
            disabled={isReadOnly}
          />
        </div>

        <div className="my-6">
          <DocumentFileInput
            label={t('stock.receipts.form.receipt-number')}
            name="receiptFile"
            value={formik.values.receiptFile}
            setFieldValue={(field, fileValue) => {
              formik.setFieldValue(field, fileValue);
              if (fileValue) setRemovedReceiptFile(false);
            }}
            isSubmitting={formik.isSubmitting}
            touched={formik.touched.receiptFile}
            errors={formik.errors.receiptFile}
            accept=".pdf,.jpg,.jpeg,.png,.csv,.doc,.docx,.xls,.xlsx"
            existingUrl={removedReceiptFile ? null : reception?.receiptFileUrl}
            existingFileLabel={t('stock.receipts.form.current-file')}
            onRemoveExisting={() => setRemovedReceiptFile(true)}
            disabled={isReadOnly}
          />
        </div>

        <TextField
          margin="dense"
          fullWidth
          multiline
          minRows={3}
          name="observations"
          label={t('observations')}
          value={formik.values.observations}
          onChange={formik.handleChange}
        />

        <h2>{t('stock.forms.products')}</h2>

        {products.map((product, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #DDD',
              padding: 15,
              marginBottom: 15,
            }}
          >
            <SelectField
              name={`product-${index}`}
              label={t('stock.receipts.form.product-name-sku')}
              placeholder={t('stock.forms.select')}
              value={product.globalProductId}
              options={catalogProducts.map((p) => ({
                value: p.globalProductId,
                label: `${p.name} (${p.sku ?? ''})`,
              }))}
              isMulti={false}
              setFieldValue={(_, value) => {
                selectCatalogProduct(index, value);
              }}
              error={false}
              isDisabled={false}
              backspaceRemovesValue
              onBlur={() => undefined}
              touched={true}
              isClearable
              isSearchable
            />

            <TextField
              margin="dense"
              fullWidth
              label={t('stock.forms.sku')}
              value={product.sku}
              onChange={(e) => {
                const normalized = e.target.value.replace(/\s+/g, '-').slice(0, 100);
                updateProduct(index, 'sku', normalized);
              }}
              inputProps={{
                maxLength: 100,
              }}
            />

            <TextField
              margin="dense"
              fullWidth
              label={t('stock.forms.name')}
              value={product.name}
              onChange={(e) => updateProduct(index, 'name', e.target.value)}
            />

            <SelectField
              name={`product-type-${index}`}
              label={t('stock.forms.type')}
              placeholder={t('select')}
              value={product.type}
              options={[
                {
                  label: t('stock.forms.finished-product'),
                  value: StockProductType.FINISHED,
                },
                {
                  label: t('stock.forms.raw-material'),
                  value: StockProductType.RAW_MATERIAL,
                },
              ]}
              isMulti={false}
              setFieldValue={(_, value) => {
                updateProduct(index, 'type', value);
              }}
              error={false}
              isDisabled={false}
              backspaceRemovesValue
              onBlur={() => undefined}
              touched={true}
              isClearable={false}
              isSearchable={false}
            />

            {(() => {
              const isUnitsReadOnly = product.globalProductId !== null || Boolean(isReadOnly);
              const unitMeasureOptions = Object.values(UnitOfMeasure);

              const fieldStyle = {
                flex: '0 0 50%',
                maxWidth: '50%',
              };

              const rowStyle = {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
              };

              return (
                <>
                  <div style={rowStyle}>
                    <div style={fieldStyle}>
                      <TextField
                        margin="dense"
                        fullWidth
                        select
                        label={t('stock.receipts.form.reception-unit')}
                        value={product.receptionUnitMeasure ?? UnitOfMeasure.UNIT}
                        onChange={(e) =>
                          updateProduct(index, 'receptionUnitMeasure', e.target.value || UnitOfMeasure.UNIT)
                        }
                        disabled={isUnitsReadOnly || product.type !== StockProductType.RAW_MATERIAL}
                        helperText={t('stock.receipts.form.reception-unit-helper')}
                      >
                        {unitMeasureOptions.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {t(`stock.units.${unit}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div style={fieldStyle}>
                      <TextField
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{
                          min: 0,
                        }}
                        label={t('stock.forms.quantity')}
                        value={product.quantity}
                        onChange={(e) => updateProduct(index, 'quantity', Number(e.target.value))}
                      />
                    </div>
                  </div>

                  {product.type === StockProductType.RAW_MATERIAL && (
                    <div style={rowStyle}>
                      <div style={fieldStyle}>
                        <TextField
                          margin="dense"
                          fullWidth
                          select
                          label={t('stock.receipts.form.consumption-unit')}
                          value={product.unitMeasure ?? ''}
                          onChange={(e) => updateProduct(index, 'unitMeasure', e.target.value || null)}
                          disabled={isUnitsReadOnly}
                          helperText={t('stock.receipts.form.consumption-unit-helper')}
                        >
                          <MenuItem value="">
                            <em>{t('stock.forms.select')}</em>
                          </MenuItem>
                          {unitMeasureOptions.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {t(`stock.units.${unit}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                      <div style={fieldStyle}>
                        <TextField
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{
                            min: 0,
                            step: 0.01,
                          }}
                          label={t('stock.receipts.form.units-per-reception-unit')}
                          value={product.unitsPerReceptionUnit ?? 1}
                          onChange={(e) => updateProduct(index, 'unitsPerReceptionUnit', Number(e.target.value))}
                          disabled={isUnitsReadOnly}
                          helperText={t('stock.receipts.form.units-per-reception-unit-helper')}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <TextField
              margin="dense"
              fullWidth
              type="number"
              inputProps={{
                min: 0,
                max: 99999,
              }}
              label={t('stock.receipts.form.unit-cost')}
              value={product.unitCost}
              onChange={(e) => updateProduct(index, 'unitCost', Number(e.target.value))}
            />

            <div
              style={{
                marginTop: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <strong>
                {t('stock.receipts.table.total')}: {formatMoneyAuto(product.quantity * product.unitCost)}
              </strong>

              <div>
                <IconButton color="primary" onClick={() => clearProduct(index)}>
                  <Delete />
                </IconButton>

                <IconButton color="primary" onClick={addProduct}>
                  <Add />
                </IconButton>

                {products.length > 1 && (
                  <IconButton color="secondary" onClick={() => removeProduct(index)}>
                    <Delete />
                  </IconButton>
                )}
              </div>
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: 20,
            padding: 15,
            background: '#F5F5F5',
            borderRadius: 4,
          }}
        >
          <h3>
            {t('stock.receipts.form.total-receipt')}: {formatMoneyAuto(totalReceipt)}
          </h3>
        </div>

        <SaveError error={saveError} />

        {!isReadOnly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 25,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={formik.isSubmitting || products.length === 0}
            >
              {reception ? t('stock.receipts.form.save-changes') : t('stock.receipts.form.create')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
};
